import {createContext, use, useMemo} from 'react'
import type {Session} from '../../types/session'

type SessionLogsVariant = 'default' | 'mission-control' | 'chat'

/**
 * context to optionally allow consumers of SessionLogs to modify its behavior based on the variant
 * without Provider it returns default
 */
type Context = {
  variant: SessionLogsVariant
  onToolExpand: null | ((expand: boolean) => void)
  session: Session | null
  sessionNwo: string | null
  /**
   * Optional override for how a user message's text content is rendered. Applies to
   * every `UserMessageBubble` rendered under this provider. Return `null`/`undefined`
   * to fall back to the default plain-text rendering for that message.
   */
  renderUserMessageContent: null | ((content: string, options: {collapsible: boolean}) => React.ReactNode)
  alignToolbarWithMessage: boolean
}

export const SessionLogsContext = createContext<Context>({
  variant: 'default',
  onToolExpand: null,
  session: null,
  sessionNwo: null,
  renderUserMessageContent: null,
  alignToolbarWithMessage: false,
})

export function useSessionLogsContext() {
  return use(SessionLogsContext)
}

type SessionLogsProviderProps = {
  variant: SessionLogsVariant
  children: React.ReactNode
  onToolExpand?: Context['onToolExpand']
  session?: Session | null
  sessionNwo?: string | null
  renderUserMessageContent?: Context['renderUserMessageContent']
  alignToolbarWithMessage?: boolean
}

export function SessionLogsProvider({
  variant,
  onToolExpand = null,
  session = null,
  sessionNwo = null,
  renderUserMessageContent = null,
  alignToolbarWithMessage = false,
  children,
}: SessionLogsProviderProps) {
  return (
    <SessionLogsContext
      value={useMemo(
        () => ({
          variant,
          onToolExpand,
          session,
          sessionNwo,
          renderUserMessageContent,
          alignToolbarWithMessage,
        }),
        [variant, onToolExpand, session, sessionNwo, renderUserMessageContent, alignToolbarWithMessage],
      )}
    >
      {children}
    </SessionLogsContext>
  )
}
